import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Scale, Shield, CreditCard, Users, AlertTriangle, FileText, Mail } from 'lucide-react';

const TermsPage = () => {
  const { i18n } = useTranslation();
  const lastUpdate = "27 février 2026";
  
  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary/10 to-background py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Scale className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl font-bold font-serif mb-4">Conditions Générales d'Utilisation</h1>
            <p className="text-gray-600">Dernière mise à jour : {lastUpdate}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl shadow-lg p-8 md:p-12"
          >
            {/* Important Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-8 rounded-r-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-semibold text-amber-800">Important</p>
                  <p className="text-amber-700 text-sm">
                    En utilisant XPerience, vous acceptez les présentes conditions. Veuillez les lire attentivement avant toute utilisation de la plateforme.
                  </p>
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none space-y-8">
              
              {/* Article 1 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-primary" />
                  Article 1 – Définitions
                </h2>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>"Plateforme"</strong> : désigne le site internet XPerience accessible à l'adresse xperience-guides.com et ses applications associées.</li>
                  <li><strong>"Exploitant"</strong> : désigne le propriétaire et exploitant de la Plateforme, domicilié en Suisse.</li>
                  <li><strong>"Guide" ou "Explorateur"</strong> : désigne toute personne physique inscrite sur la Plateforme proposant des expériences ou prestations de guide.</li>
                  <li><strong>"Voyageur" ou "Expérimentateur"</strong> : désigne toute personne physique inscrite sur la Plateforme souhaitant réserver une expérience.</li>
                  <li><strong>"Utilisateur"</strong> : désigne tout Guide ou Voyageur inscrit sur la Plateforme.</li>
                  <li><strong>"Expérience"</strong> : désigne toute prestation de service proposée par un Guide sur la Plateforme.</li>
                  <li><strong>"Commission"</strong> : désigne le pourcentage prélevé par la Plateforme sur chaque transaction payante.</li>
                </ul>
              </section>

              {/* Article 2 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Shield className="w-6 h-6 text-primary" />
                  Article 2 – Objet et rôle de la Plateforme
                </h2>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 my-4">
                  <p className="text-blue-800 font-medium">
                    XPerience agit exclusivement en qualité d'intermédiaire technique de mise en relation entre Guides et Voyageurs.
                  </p>
                </div>
                <p className="text-gray-700">
                  La Plateforme n'est en aucun cas partie aux contrats conclus entre les Guides et les Voyageurs. 
                  Elle ne fournit pas elle-même de prestations de guide, d'hébergement, de transport ou toute autre prestation proposée par les Guides.
                </p>
                <p className="text-gray-700 mt-4">
                  Les Guides exercent leur activité de manière <strong>indépendante</strong> et ne sont en aucun cas employés, agents ou représentants de XPerience.
                </p>
              </section>

              {/* Article 3 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Users className="w-6 h-6 text-primary" />
                  Article 3 – Inscription et acceptation des conditions
                </h2>
                
                <h3 className="text-lg font-semibold mt-4">3.1 Acceptation des conditions</h3>
                <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500 my-4">
                  <p className="text-amber-800">
                    <strong>En créant un compte sur XPerience, l'Utilisateur déclare avoir lu, compris et accepté sans réserve les présentes Conditions Générales d'Utilisation.</strong>
                  </p>
                  <p className="text-amber-700 mt-2 text-sm">
                    Cette acceptation est matérialisée par le cochage de la case "J'accepte les conditions d'utilisation" lors de l'inscription.
                  </p>
                </div>
                
                <h3 className="text-lg font-semibold mt-4">3.2 Conditions générales d'accès</h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>L'inscription est réservée aux personnes physiques <strong>âgées d'au moins 18 ans</strong> et jouissant de leur pleine capacité juridique.</li>
                  <li>L'Utilisateur s'engage à fournir des informations exactes, complètes et à jour lors de son inscription.</li>
                  <li>Chaque Utilisateur ne peut détenir qu'un seul compte.</li>
                  <li>L'Utilisateur est responsable de la confidentialité de ses identifiants de connexion.</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4">3.3 Conditions spécifiques aux Guides</h3>
                <p className="text-gray-700">En s'inscrivant comme Guide, l'Utilisateur déclare et garantit :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Disposer de toutes les autorisations, licences et assurances nécessaires à l'exercice de son activité selon la législation applicable dans son pays.</li>
                  <li>Exercer son activité en conformité avec les lois fiscales et sociales en vigueur.</li>
                  <li>Avoir pris connaissance et accepté la <a href="/guide-charter" className="text-primary hover:underline">Charte des Guides</a>.</li>
                  <li><strong>S'engager à honorer toutes les réservations confirmées</strong> (voir Article 4.5 pour les sanctions).</li>
                </ul>
              </section>

              {/* Article 4 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <CreditCard className="w-6 h-6 text-primary" />
                  Article 4 – Paiement, Annulation et Commission
                </h2>
                
                <h3 className="text-lg font-semibold mt-4">4.1 Système de paiement sécurisé</h3>
                <p className="text-gray-700">
                  Tous les paiements transitent par la Plateforme via notre prestataire de paiement sécurisé (Stripe). 
                  <strong> Aucun paiement direct entre Voyageur et Guide n'est autorisé.</strong>
                </p>
                
                <h3 className="text-lg font-semibold mt-4">4.2 Processus de paiement</h3>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li><strong>Réservation</strong> : Le Voyageur paie l'intégralité du montant lors de la réservation.</li>
                  <li><strong>Blocage des fonds</strong> : Les fonds sont conservés par la Plateforme jusqu'à la réalisation de l'expérience.</li>
                  <li><strong>Validation</strong> : Le Voyageur confirme la réalisation de l'expérience sur la Plateforme.</li>
                  <li><strong>Délai de contestation</strong> : Un délai de 48 heures est accordé au Voyageur pour signaler tout litige.</li>
                  <li><strong>Versement au Guide</strong> : Passé ce délai sans contestation, le paiement est versé au Guide, déduction faite de la commission.</li>
                </ol>
                
                <h3 className="text-lg font-semibold mt-4">4.3 Modèles de tarification</h3>
                
                <div className="bg-gradient-to-r from-primary/10 to-orange-50 p-5 rounded-xl border border-primary/20 my-4">
                  <h4 className="font-bold text-gray-800 mb-3">XPerience propose trois types de contenus :</h4>
                  
                  <div className="space-y-4">
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">💰</span>
                        <strong className="text-primary">Expériences payantes</strong>
                      </div>
                      <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
                        <li>Activités organisées par les Guides contre rémunération</li>
                        <li>Le Voyageur paie le prix affiché lors de la réservation</li>
                        <li><strong>Commission plateforme : 15%</strong> prélevée sur chaque transaction</li>
                        <li>Cette commission couvre : paiement sécurisé, assurance, support, fonctionnement</li>
                        <li>Inscription gratuite pour le Guide</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">🤝</span>
                        <strong className="text-green-600">Meetups gratuits</strong>
                      </div>
                      <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
                        <li>Rencontres et événements communautaires proposés gratuitement par les Guides</li>
                        <li><strong>Aucun frais</strong> pour le Voyageur ni pour le Guide</li>
                        <li>Les participants peuvent faire une <strong>donation volontaire</strong> pour soutenir la plateforme</li>
                        <li>Les donations sont facultatives et ne conditionnent pas l'accès au Meetup</li>
                        <li>Le Guide ne perçoit aucune rémunération via la plateforme pour les Meetups</li>
                      </ul>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">🎪</span>
                        <strong className="text-purple-600">Événements locaux</strong>
                      </div>
                      <ul className="list-disc pl-6 text-gray-700 text-sm space-y-1">
                        <li>Festivals, fêtes locales, marchés et événements communautaires</li>
                        <li><strong>Publication gratuite</strong> pour les organisateurs d'événements</li>
                        <li>Permet de promouvoir les événements locaux auprès des voyageurs</li>
                        <li>Les utilisateurs peuvent faire une <strong>donation volontaire</strong> pour soutenir la plateforme</li>
                        <li>Aucune commission prélevée – XPerience soutient la vie locale</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold mt-4">4.4 Donations volontaires</h3>
                <p className="text-gray-700">
                  Les donations effectuées par les Voyageurs suite à un Meetup gratuit sont :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li><strong>Entièrement volontaires</strong> – Aucune obligation de donner</li>
                  <li><strong>Non remboursables</strong> – Sauf en cas d'annulation du Meetup par le Guide</li>
                  <li><strong>Destinées à la plateforme</strong> – Pour financer son fonctionnement et son développement</li>
                  <li><strong>Distinctes des paiements</strong> – Ne constituent pas une rémunération pour le Guide</li>
                </ul>
                
                <h3 className="text-lg font-semibold mt-4">4.5 Système de paiement sécurisé</h3>
                <p className="text-gray-700">
                  Tous les paiements transitent par la Plateforme via notre prestataire de paiement sécurisé (Stripe). 
                  <strong> Aucun paiement direct entre Voyageur et Guide n'est autorisé.</strong>
                </p>
                
                <h3 className="text-lg font-semibold mt-4">4.6 Processus de paiement</h3>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li><strong>Réservation</strong> : Le Voyageur paie l'intégralité du montant lors de la réservation.</li>
                  <li><strong>Blocage des fonds</strong> : Les fonds sont conservés par la Plateforme jusqu'à la réalisation de l'expérience.</li>
                  <li><strong>Validation</strong> : Le Voyageur confirme la réalisation de l'expérience sur la Plateforme.</li>
                  <li><strong>Délai de contestation</strong> : Un délai de 48 heures est accordé au Voyageur pour signaler tout litige.</li>
                  <li><strong>Versement au Guide</strong> : Passé ce délai sans contestation, le paiement est versé au Guide, déduction faite de la commission.</li>
                </ol>
                
                <h3 className="text-lg font-semibold mt-4">4.7 Politique d'annulation par le Voyageur</h3>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 my-4">
                  <p className="text-blue-800 font-medium mb-2">Conditions d'annulation :</p>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b border-blue-200">
                        <td className="py-2 text-blue-700"><strong>Plus de 24h avant</strong> l'expérience</td>
                        <td className="py-2 text-blue-800"><strong>Remboursement intégral (100%)</strong></td>
                      </tr>
                      <tr>
                        <td className="py-2 text-blue-700"><strong>Moins de 24h avant</strong> l'expérience</td>
                        <td className="py-2 text-blue-800"><strong>Aucun remboursement (0%)</strong> – Le montant total est dû</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-gray-700 text-sm">
                  L'heure de référence est l'heure de début de l'expérience telle qu'indiquée dans la réservation. 
                  L'annulation doit être effectuée via la Plateforme pour être prise en compte.
                </p>
                
                <h3 className="text-lg font-semibold mt-4">4.8 Annulation par le Guide et sanctions</h3>
                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500 my-4">
                  <p className="text-red-800 font-medium">En cas d'annulation par le Guide :</p>
                  <ul className="list-disc pl-6 mt-2 text-red-700 space-y-1">
                    <li><strong>Remboursement intégral (100%)</strong> au Voyageur, quelle que soit la date d'annulation</li>
                    <li><strong>1ère annulation</strong> : Avertissement formel au Guide</li>
                    <li><strong>2ème annulation</strong> : Suspension temporaire du compte (7 jours)</li>
                    <li><strong>3ème annulation</strong> : Suspension prolongée (30 jours) ou suppression définitive du compte</li>
                  </ul>
                </div>
                <p className="text-gray-700 text-sm">
                  Les Guides sont tenus d'honorer leurs engagements. Les annulations répétées nuisent à la confiance des Voyageurs et à la réputation de la Plateforme.
                </p>
                
                <h3 className="text-lg font-semibold mt-4">4.9 Procédure de remboursement</h3>
                <p className="text-gray-700">Pour demander un remboursement :</p>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                  <li>Connectez-vous à votre compte XPerience</li>
                  <li>Accédez à "Mes réservations"</li>
                  <li>Sélectionnez la réservation concernée</li>
                  <li>Cliquez sur "Annuler la réservation"</li>
                  <li>Le remboursement est traité sous 5 à 10 jours ouvrés selon votre banque</li>
                </ol>
              </section>

              {/* Article 5 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 5 – Obligations des Guides
                </h2>
                <p className="text-gray-700">Le Guide s'engage à :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Fournir des descriptions exactes et non trompeuses de ses expériences.</li>
                  <li>Honorer toutes les réservations confirmées.</li>
                  <li>Respecter les horaires et conditions convenus.</li>
                  <li>Assurer la sécurité des Voyageurs dans la limite du raisonnable.</li>
                  <li>Informer les Voyageurs des risques potentiels liés à l'expérience.</li>
                  <li>Disposer des qualifications nécessaires pour les activités proposées.</li>
                  <li>Se conformer à toutes les lois et réglementations applicables.</li>
                </ul>
                
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500 mt-4">
                  <p className="text-green-800">
                    <strong>Recommandation :</strong> Il est fortement recommandé aux Guides de souscrire une assurance responsabilité civile professionnelle couvrant leur activité.
                  </p>
                </div>
              </section>

              {/* Article 6 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 6 – Obligations des Voyageurs
                </h2>
                <p className="text-gray-700">Le Voyageur s'engage à :</p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Fournir des informations exactes lors de la réservation.</li>
                  <li>Se présenter aux horaires convenus.</li>
                  <li>Respecter les consignes de sécurité données par le Guide.</li>
                  <li>Adopter un comportement respectueux envers le Guide et les autres participants.</li>
                  <li>Informer le Guide de toute condition médicale pertinente.</li>
                  <li>Confirmer la réalisation de l'expérience sur la Plateforme.</li>
                </ul>
                
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mt-4">
                  <p className="text-blue-800">
                    <strong>Recommandation :</strong> Il est conseillé aux Voyageurs de souscrire une assurance voyage couvrant les activités prévues.
                  </p>
                </div>
              </section>

              {/* Article 7 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 7 – Litiges et Médiation
                </h2>
                
                <h3 className="text-lg font-semibold mt-4">7.1 Engagement des parties</h3>
                <p className="text-gray-700">
                  Chaque partie (Guide et Voyageur) s'engage à respecter ses obligations telles que définies dans les présentes CGU. 
                  En cas de manquement, la partie lésée peut initier une procédure de réclamation.
                </p>
                
                <h3 className="text-lg font-semibold mt-4">7.2 Procédure de réclamation</h3>
                <div className="bg-gray-50 p-4 rounded-lg my-4">
                  <p className="font-medium text-gray-800 mb-2">Étapes à suivre :</p>
                  <ol className="list-decimal pl-6 space-y-2 text-gray-700">
                    <li><strong>Délai</strong> : Le Voyageur dispose de <strong>48 heures</strong> après la date prévue de l'expérience pour signaler un problème.</li>
                    <li><strong>Déclaration</strong> : Connectez-vous à votre compte et accédez à la réservation concernée.</li>
                    <li><strong>Motif</strong> : Décrivez précisément le problème rencontré (non-présentation du Guide, prestation non conforme, etc.).</li>
                    <li><strong>Preuves</strong> : Fournissez si possible des éléments à l'appui (photos, messages, etc.).</li>
                  </ol>
                </div>
                
                <h3 className="text-lg font-semibold mt-4">7.3 Processus de médiation</h3>
                <p className="text-gray-700">
                  L'Exploitant s'efforcera de résoudre tout litige à l'amiable selon le processus suivant :
                </p>
                <ol className="list-decimal pl-6 space-y-2 text-gray-700 mt-2">
                  <li><strong>Réception</strong> : La réclamation est enregistrée et un accusé de réception est envoyé sous 24h.</li>
                  <li><strong>Enquête</strong> : L'Exploitant contacte les deux parties pour recueillir leurs versions des faits.</li>
                  <li><strong>Analyse</strong> : L'Exploitant examine les éléments fournis par chaque partie.</li>
                  <li><strong>Décision</strong> : Une décision est rendue sous 7 jours ouvrés maximum.</li>
                </ol>
                
                <h3 className="text-lg font-semibold mt-4">7.4 Résolutions possibles</h3>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <p className="font-semibold text-green-800">Remboursement intégral</p>
                    <p className="text-green-700 text-sm mt-1">100% remboursé au Voyageur</p>
                    <p className="text-green-600 text-xs mt-2">Ex: Guide absent, prestation non réalisée</p>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                    <p className="font-semibold text-amber-800">Remboursement partiel</p>
                    <p className="text-amber-700 text-sm mt-1">25% à 75% selon la gravité</p>
                    <p className="text-amber-600 text-xs mt-2">Ex: Prestation écourtée ou partiellement conforme</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <p className="font-semibold text-red-800">Rejet de la réclamation</p>
                    <p className="text-red-700 text-sm mt-1">Paiement versé au Guide</p>
                    <p className="text-red-600 text-xs mt-2">Ex: Réclamation infondée ou abusive</p>
                  </div>
                </div>
                
                <h3 className="text-lg font-semibold mt-4">7.5 Caractère définitif</h3>
                <p className="text-gray-700">
                  Les décisions rendues par l'Exploitant dans le cadre de la médiation sont <strong>définitives et sans appel</strong>. 
                  En acceptant les présentes CGU, les parties renoncent à contester ces décisions.
                </p>
                
                <h3 className="text-lg font-semibold mt-4">7.6 Recours judiciaire</h3>
                <p className="text-gray-700">
                  Si la médiation échoue ou si une partie n'accepte pas la décision, le litige pourra être porté devant les tribunaux compétents de Fribourg, Suisse (voir Article 13).
                </p>
              </section>

              {/* Article 8 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <AlertTriangle className="w-6 h-6 text-primary" />
                  Article 8 – Limitation de responsabilité
                </h2>
                <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500 my-4">
                  <p className="text-red-800 font-medium">
                    La Plateforme agissant comme simple intermédiaire, l'Exploitant décline toute responsabilité concernant :
                  </p>
                </div>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>La qualité, la sécurité ou la légalité des expériences proposées par les Guides.</li>
                  <li>La véracité des informations fournies par les Utilisateurs.</li>
                  <li>Les dommages directs ou indirects résultant de l'utilisation de la Plateforme.</li>
                  <li>Les accidents, blessures ou décès survenus lors des expériences.</li>
                  <li>Les pertes ou vols d'objets personnels.</li>
                  <li>Les interruptions de service ou dysfonctionnements techniques.</li>
                  <li>Les actes ou omissions des Guides ou des Voyageurs.</li>
                </ul>
                <p className="text-gray-700 mt-4">
                  En tout état de cause, la responsabilité de l'Exploitant est limitée au montant de la commission perçue sur la transaction concernée.
                </p>
              </section>

              {/* Article 9 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 9 – Propriété intellectuelle
                </h2>
                <p className="text-gray-700">
                  La Plateforme, son contenu (textes, images, logos, design) et sa structure sont protégés par les droits de propriété intellectuelle. 
                  Toute reproduction, représentation ou exploitation non autorisée est interdite.
                </p>
                <p className="text-gray-700 mt-4">
                  Les Utilisateurs conservent la propriété de leurs contenus publiés sur la Plateforme mais accordent à l'Exploitant une licence non exclusive d'utilisation à des fins de promotion de la Plateforme.
                </p>
              </section>

              {/* Article 10 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 10 – Protection des données personnelles
                </h2>
                <p className="text-gray-700">
                  L'Exploitant s'engage à respecter la législation suisse en matière de protection des données (LPD) ainsi que le Règlement européen sur la protection des données (RGPD) pour les utilisateurs européens.
                </p>
                <p className="text-gray-700 mt-4">
                  Pour plus d'informations, veuillez consulter notre <a href="/privacy" className="text-primary hover:underline">Politique de confidentialité</a>.
                </p>
              </section>

              {/* Article 11 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 11 – Résiliation et suspension
                </h2>
                <p className="text-gray-700">
                  L'Exploitant se réserve le droit de suspendre ou supprimer tout compte d'Utilisateur, sans préavis ni indemnité, en cas de :
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>Violation des présentes CGU</li>
                  <li>Comportement frauduleux ou malhonnête</li>
                  <li>Évaluations négatives répétées</li>
                  <li>Non-respect de la Charte des Guides</li>
                  <li>Activité illégale</li>
                </ul>
              </section>

              {/* Article 12 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2">
                  Article 12 – Modification des CGU
                </h2>
                <p className="text-gray-700">
                  L'Exploitant se réserve le droit de modifier les présentes CGU à tout moment. 
                  Les Utilisateurs seront informés par email ou notification sur la Plateforme. 
                  La poursuite de l'utilisation de la Plateforme après modification vaut acceptation des nouvelles conditions.
                </p>
              </section>

              {/* Article 13 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Scale className="w-6 h-6 text-primary" />
                  Article 13 – Droit applicable et juridiction
                </h2>
                <div className="bg-gray-100 p-6 rounded-lg mt-4">
                  <p className="text-gray-800 font-medium mb-4">
                    Les présentes CGU sont régies par le <strong>droit suisse</strong>.
                  </p>
                  <p className="text-gray-800">
                    Tout litige relatif à l'interprétation ou l'exécution des présentes sera soumis à la compétence exclusive des <strong>tribunaux de Fribourg, Suisse</strong>.
                  </p>
                </div>
              </section>

              {/* Article 14 */}
              <section>
                <h2 className="text-2xl font-bold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Mail className="w-6 h-6 text-primary" />
                  Article 14 – Contact
                </h2>
                <p className="text-gray-700">
                  Pour toute question relative aux présentes CGU, vous pouvez nous contacter :
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-700"><strong>XPerience</strong></p>
                  <p className="text-gray-700">Fribourg, Suisse</p>
                  <p className="text-gray-700">Email : contact@xperience-guides.com</p>
                </div>
              </section>

            </div>

            {/* Footer */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center">
              <p className="text-gray-500 text-sm">
                © 2026 XPerience - Tous droits réservés
              </p>
              <p className="text-gray-400 text-xs mt-2">
                Dernière mise à jour : {lastUpdate}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TermsPage;
